/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import triggerToast from './triggerToast';

const SERVER_ERROR_KEYWORDS = [
  'HTTPSConnectionPool',
  "'str' object has no attribute 'get'",
  'Request failed with status code 400',
  "`<class 'NoneType'>`",
  'NoneType',
  'Cannot read properties of null',
  'object has no attribute',
  'NoneType',
];

const DEFAULT_ERROR_MSG = 'Something went wrong. Please try again later';

const extractErrMsgFromErr = (error) => {
  try {
    // Priority 1: Handle specific API error structures (most specific first)

    // 1.1: Handle detail property (most common API error format)
    if (error?.response?.data?.detail) {
      // Handle array-type detail: { detail: [{ msg: "error" }] }
      if (Array.isArray(error.response.data.detail)) {
        const message =
          error.response.data.detail[0]?.msg || error.response.data.detail[0]?.message;
        if (message) {
          return message;
        }
      }
      // Handle object-type detail: { detail: { message: "error", error_code: "VAL001" } }
      else if (
        typeof error.response.data.detail === 'object' &&
        error.response.data.detail !== null
      ) {
        const errorMsg = error.response.data.detail?.message;
        if (errorMsg) {
          return errorMsg;
        }
      }
      // Handle string-type detail: { detail: "error message" }
      else if (typeof error.response.data.detail === 'string') {
        return error.response.data.detail;
      }
    }

    // 1.2: Handle errorMessage property: { errorMessage: "error" }
    if (error?.response?.data?.errorMessage) {
      return error?.response?.data?.errorMessage;
    }

    // 1.3: Handle message property: { message: "error" }
    if (error?.response?.data?.message) {
      return error?.response?.data?.message;
    }

    // 1.4: Handle response.data.errors structure: { errors: [{ error_message: "error" }] }
    if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
      const {errors} = error.response.data;

      if (errors && errors.length > 0) {
        let errorMsg = errors[0].error_message || errors[0].message;

        if (errorMsg && !SERVER_ERROR_KEYWORDS.some((keyword) => errorMsg.includes(keyword))) {
          return errorMsg;
        }
      }
    }

    // 1.5: Handle string response data: "error message"
    if (typeof error?.response?.data === 'string') {
      const errorMsg = error.response.data;
      if (!SERVER_ERROR_KEYWORDS.some((keyword) => errorMsg.includes(keyword))) {
        return errorMsg;
      }
    }

    // Priority 2: Handle GraphQL specific errors

    // 2.1: Handle GraphQL errors array: { data: { errors: [{ message: "error" }] } }
    if (error?.data?.errors?.[0]?.message) {
      return error.data.errors[0].message;
    }

    // Priority 3: Handle response-level errors

    // 3.1: Handle response message: { response: { message: "error" } }
    if (error?.response?.message) {
      const errorMsg = error.response.message;
      if (!SERVER_ERROR_KEYWORDS.some((keyword) => errorMsg.includes(keyword))) {
        return errorMsg;
      }
    }

    // Priority 4: Handle top-level error properties (lowest priority)

    // 4.1: Handle direct error message (Apollo/Network errors): { message: "error" }
    if (error?.message) {
      const errorMsg = error.message;
      if (!SERVER_ERROR_KEYWORDS.some((keyword) => errorMsg.includes(keyword))) {
        return errorMsg;
      }
    }
  } catch (error) {
    console.error('Error extracting error message:', error);
    return DEFAULT_ERROR_MSG;
  }

  return DEFAULT_ERROR_MSG;
};
const handleApiError = (err, toastType = 'error', options = {}) => {
  const errorMessage = extractErrMsgFromErr(err);

  // If we got a meaningful error message, show it
  if (errorMessage && errorMessage !== DEFAULT_ERROR_MSG) {
    if (!SERVER_ERROR_KEYWORDS.some((keyword) => errorMessage.includes(keyword))) {
      triggerToast(errorMessage, toastType, options);
      return;
    }
    triggerToast(errorMessage, toastType, options);
    return;
  }

  // If no meaningful error message found, fall back to status code handling
  if (err && err.response && err.response.status) {
    const errorCode = parseInt(err.response.status);
    handleStatusCodeError(errorCode, toastType, options);
  } else {
    triggerToast(DEFAULT_ERROR_MSG, toastType, options);
  }
};

const handleStatusCodeError = (errorCode, toastType, options) => {
  switch (errorCode) {
    case 400:
      triggerToast('Token expired, please re-initiate your KYC', toastType, options);
      break;
    case 500:
      triggerToast('CVL is down, please try again after sometime', toastType, options);
      break;
    case 401:
      triggerToast('Credentials mismatch, please re-initiate your KYC', toastType, options);
      break;
    case 422:
      triggerToast('You are not a validated user, please re-initiate your KYC', toastType, options);
      break;
    case 502:
      triggerToast('Server timeout, please try again after sometime', toastType, options);
      break;
    default:
      triggerToast('Something went wrong. Please try again', toastType, options);
  }
};

export default handleApiError;
export {extractErrMsgFromErr};
